import React, {useEffect, useMemo} from "react";
import {useQuery} from "@apollo/client";
import gql from "graphql-tag";
import ReactDocumentTitle from "../../../components/ReactDocumentTitle";
import AppLayout from "../../../layouts/AppLayout";
import {Event, EventOrderByInput, EventType, ReportConfig, Trainer} from "../../../schema";
import {SortingRule} from "react-table";
import Table, {TableFilter} from "../../../ui/table/Table";
import H1 from "../../../ui/H1";
import {FilterState} from "../../../ui/table/TableFilter";
import Location from "../../../models/event/Location";
import ScheduleFormatter from "../../../models/formatter/ScheduleFormatter";
import Schedule from "../../../models/event/Schedule";
import {SelectFilterBlock, SelectValue} from "../../../ui/table/filter/UISelectFilterBlock";
import Modal from "../../../ui/modal/Modal";
import PrimaryButton from "../../../ui/PrimaryButton";
import {externalUrl} from "../../../utils";
import {RadioFilterBlock} from "../../../ui/table/filter/UIRadioFilterBlock";
import {DateTime} from "luxon";
import {ExportEventForm} from "../../../components/event/ExportEventForm";

type TableData = {
  id: string
  title: React.ReactNode
  location: React.ReactNode
  schedule: React.ReactNode
  free: string
  numberOfAttendees: number
  confirmed: string
  trainers: React.ReactNode
}

function fromEvent(event: Event): TableData {
  return {
    id: event.id,
    title: <span className="whitespace-normal">{event.title}</span>,
    location: <span className="whitespace-normal">{new Location(event.location).toString()}</span>,
    schedule: ScheduleFormatter.format('en', new Schedule(event.schedule), 'interval'),
    free: event.isFree ? 'Yes' : 'No',
    numberOfAttendees: event.statistics.students,
    confirmed: event.confirmed ? 'Yes' : 'No',
    trainers: <div className="flex flex-col">{event.trainers.map((x) => <span key={x.id}>{x.name}</span>)}</div>
  }
}

function getSortBy(sortBy: SortingRule<Event>[]): EventOrderByInput {
  if (sortBy.length > 0) {
    switch (sortBy[0]?.id) {
      case 'title':
        return sortBy[0]?.desc ? EventOrderByInput.title_desc : EventOrderByInput.title_asc;
      default:
        return sortBy[0]?.desc ? EventOrderByInput.start_desc : EventOrderByInput.start_asc;
    }
  }
  return sortBy[0]?.desc ? EventOrderByInput.start_desc : EventOrderByInput.start_asc;
}

export default function BrandEventListScene() {

  const skipPageResetRef = React.useRef<boolean>();
  const [attendees, events] = React.useState<TableData[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(1);
  const [skip, setSkip] = React.useState<number>(0);
  const [first, setFirst] = React.useState<number>(25);
  const [sortBy, setSortBy] = React.useState<EventOrderByInput>(EventOrderByInput.start_desc);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(undefined);
  const [skipDataLoad, setSkipDataLoad] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);

  const publicPrivate = new FilterState('publicPrivate', false, '');
  const trainersState = new FilterState('trainer', false, '');
  const datesState = new FilterState('dates', true, 'upcoming');
  const typesState = new FilterState('eventType', false, '');
  const stateStates = new FilterState('state', false, '');
  const [states, setStates] = React.useState<FilterState[]>([typesState, publicPrivate, trainersState, datesState, stateStates]);
  const selectedTypeId = states.find(x => x.id === 'eventType' && x.active)?.value;
  const publicPrivateValue = states.find(x => x.id === 'publicPrivate' && x.active)?.value;
  const selectedTrainerId = states.find(x => x.id === 'trainer' && x.active)?.value;
  const selectedDate = states.find(x => x.id === 'dates' && x.active)?.value;
  const activeValue = states.find(x => x.id === 'state' && x.active)?.value;
  const startsOn = useMemo(() => selectedDate === 'upcoming' ? DateTime.now() : undefined, [selectedDate]);
  const endsOn = useMemo(() => selectedDate === 'past' ? DateTime.now() : undefined, [selectedDate]);

  const {data, loading, error} = useQuery<EventsQuery>(query, {
    skip: skipDataLoad,
    fetchPolicy: 'cache-and-network',
    variables: {
      skip,
      first,
      sortBy,
      searchQuery,
      filterByTypeId: selectedTypeId,
      public: publicPrivateValue ? publicPrivateValue === 'public' : undefined,
      trainerId: selectedTrainerId,
      active: activeValue ? activeValue === 'active' : undefined,
      startsOn,
      endsOn
    },
  });

  useMemo(() => {
    if (data) {
      events(data.feed.events.map(x => fromEvent(x)))
      setTotalCount(data.feed.total);
    }
  }, [data])

  const onFetchData = (pageIndex: number, newPageSize: number, newSortBy: SortingRule<Event>[], filter: TableFilter | null) => {
    skipPageResetRef.current = true;
    setSkip(pageIndex * newPageSize);
    setFirst(newPageSize);
    setSearchQuery(filter?.search);
    setSortBy(getSortBy(newSortBy));
    setStates(filter?.filters || []);
    setSkipDataLoad(false);
  }

  const onClick = (event: TableData) => {
    window.location.assign(externalUrl(`/event/${event.id}`));
  }

  useEffect(() => {
    skipPageResetRef.current = false;
  }, []);

  const types: SelectValue[] = data?.types.map(x => ({id: x.id, name: x.name})) || [];
  const typeFilter = new SelectFilterBlock('eventType', 'Event Type', types);
  const trainers: SelectValue[] = data?.trainers.map(x => ({id: x.id, name: x.name})) || [];
  const trainerFilter = new SelectFilterBlock('trainer', 'Trainer', trainers);
  const publicPrivateFilter = new RadioFilterBlock('publicPrivate', 'Privacy', 'public', [{
    id: 'public',
    value: 'public',
    label: 'Public',
    checked: false,
  }, {id: 'private', value: 'private', label: 'Private', checked: false}]);
  const dateFilter = new RadioFilterBlock('dates', 'Dates', 'dates', [{
    id: 'upcoming',
    value: 'upcoming',
    label: 'Upcoming',
    checked: true,
  }, {id: 'past', value: 'past', label: 'Past', checked: false}]);
  const activeCancelledFilter = new RadioFilterBlock('state', 'State', 'state', [{
    id: 'active',
    value: 'active',
    label: 'Active',
    checked: false,
  }, {id: 'cancelled', value: 'cancelled', label: 'Cancelled', checked: false}]);

  const columns = React.useMemo(() => [
    {
      Header: 'Events',
      columns: [
        {
          Header: 'Event',
          accessor: 'title',
          filter: 'fuzzyText',
          defaultSort: 'desc',
        },
        {
          Header: 'Trainer',
          accessor: 'trainers',
          disableSortBy: true,
        },
        {
          Header: 'Location',
          accessor: 'location',
          disableSortBy: true,
        },
        {
          Header: 'Dates',
          accessor: 'schedule',
          sortType: 'basic',
        },
        {
          Header: 'Free',
          accessor: 'free',
          disableSortBy: true,
        },
        {
          Header: 'Attendees',
          accessor: 'numberOfAttendees',
          disableSortBy: true,
        }, {
          Header: 'Confirmed',
          accessor: 'confirmed',
          disableSortBy: true,
        }]
    }
  ].flatMap((c: any) => c.columns), []);

  const buttons =
    <PrimaryButton onClick={() => setOpen(true)}>Export</PrimaryButton>;

  return (
    <>
      <ReactDocumentTitle title='Events'/>

      <AppLayout className="">
        <div className="pt-8 pb-20">
          <H1>Events</H1>
          <div className="mt-6 mb-4">
            <Table<TableData, TableFilter> name="events-0.2" reset={skipPageResetRef.current || false}
                                           error={error?.message}
                                           loading={loading}
                                           total={totalCount}
                                           onFetchData={onFetchData} columns={columns}
                                           data={attendees}
                                           initialFilterState={states}
                                           filters={[typeFilter, trainerFilter, dateFilter, publicPrivateFilter, activeCancelledFilter]}
                                           buttons={buttons}
                                           onClick={onClick}/>
          </div>
          <Modal show={open} onClose={() => setOpen(false)} title="Export Events">
            {data?.exportEventConfig &&
              <ExportEventForm config={data.exportEventConfig} onClose={() => setOpen(false)}/>
            }
          </Modal>
        </div>
      </AppLayout>
    </>
  )
}

type EventFeed = {
  total: number;
  count: number;
  skip: number;
  events: Event[];
}
type EventsQuery = {
  feed: EventFeed;
  types: EventType[];
  trainers: Trainer[];
  exportEventConfig: ReportConfig;
}


const query = gql`
  query EventListQuery($skip: Int!, $first: Int!, $sortBy: EventOrderByInput!, $filterByTypeId: ID, $public: Boolean, $trainerId: ID, $startsOn: DateTime, $endsOn: DateTime, $searchQuery: String, $active: Boolean) {
    feed: brandEvents(orderBy: $sortBy, skip: $skip, count: $first, typeId: $filterByTypeId, public: $public, trainerId: $trainerId, startBeginsOn: $startsOn, endEndsOn: $endsOn, searchBy: $searchQuery, active: $active) {
      total
      count
      skip

      events {
        id
        title
        isFree
        location {
          online
          city
          country
        }
        confirmed
        schedule {
          start
          end
          timezone
        }
        trainers {
          id
          name
        }
        statistics {
          students
        }
      }
    }
    types: eventTypes {
      id
      name
    },
    trainers: trainers(onlyActive: false) {
      id
      name
    },
    exportEventConfig {
      event {
        id
        title
        selected
      },
      invoice {
        id
        title
        selected
      },
    }
  }
`;
